import React, { useContext, useState } from 'react'
import Box from '@mui/material/Box'
import { Divider, Stack, Typography } from '@mui/material'
import { ActionSheetItem, ActionSheetWrapper, Button, ContentGroup, InfoRow, Icon, theme } from '@ketch-com/deck'
import { NoteModal } from 'components/modals/rightsQueue/NoteModal'
import moment from 'moment'
import { RightsQueueViewContext } from '../../../context/RightsQueueViewContext'
import { useIsPermitted } from 'utils/hooks/useIsPermitted'
import { PERMISSIONS } from 'interfaces/permissions/permissions'
import { useAttachmentManagement, useNoteManagement } from '../hooks'
import { AttachmentsList } from 'components/attachments/AttachmentsList'
import { AttachmentsUpload } from 'components/AttachmentsUpload/AttachmentsUpload'
import { MAX_FILE_UPLOAD_SIZE_4_MB } from 'utils/constants/maxFileUploadSize'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { acceptableFileTypes } from 'pages/policyCenter/assessments/edit/constants'

export const AdditionalDetails: React.FC = () => {
  const { rightInvocation } = useContext(RightsQueueViewContext)
  const { isPermitted } = useIsPermitted()
  const { formatAttachments, handleUploadAttachment, handleDeleteAttachment } = useAttachmentManagement(rightInvocation)
  const {
    isNoteModalOpen,
    selectedNote,
    menuState,
    setState,
    setMenuState,
    handleSubmitNote,
    handleMoreMenuClick,
    handleEditNote,
    handleDeleteNote,
  } = useNoteManagement(rightInvocation)

  const [isEditing, setIsEditing] = useState(false)

  const rightInvocationNotes =
    rightInvocation?.rightInvocationNotes
      ?.filter(note => note.note)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)) || []
  const canEditDetails = isPermitted(PERMISSIONS.RTINVQ_WRITE)
  const formattedAttachments = formatAttachments(rightInvocation)

  return (
    <Box display="flex" flexDirection="column" gap={4} mb={4}>
      <ContentGroup title="Additional Details" titleVariant="h3" variant="inner-page-header" />
      <InfoRow
        title="Attachments"
        isEditable
        onEditChange={() => setIsEditing(prev => !prev)}
        onCancelChange={() => setIsEditing(false)}
      >
        {isEditing ? (
          <AttachmentsUpload
            showTitle={false}
            showAttachmentDetails
            attachments={formattedAttachments}
            canEdit={canEditDetails}
            accept={acceptableFileTypes}
            maxSize={MAX_FILE_UPLOAD_SIZE_4_MB}
            onUploadComplete={handleUploadAttachment}
            onDelete={handleDeleteAttachment}
            uploadContext={{
              version: 'resolution_attachments',
              folder: `${rightInvocation?.id}`,
              bucket: '',
            }}
          />
        ) : formattedAttachments?.length ? (
          <AttachmentsList attachments={formattedAttachments} showAttachmentDetails />
        ) : (
          <EmptyValueRenderer />
        )}
      </InfoRow>
      <InfoRow title="Notes">
        <Stack direction="row" sx={{ alignItems: 'start', justifyContent: 'space-between' }} spacing={2}>
          <Stack sx={{ width: '100%' }}>
            {rightInvocationNotes.length > 0 ? (
              rightInvocationNotes.map((item, index) => (
                <Box sx={{ width: '100%' }} key={item.note}>
                  <Stack direction="row" sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Stack>
                      <Typography>{item.note || 'None'}</Typography>
                      <Stack direction="row">
                        <Typography color={theme.palette.Text.Secondary} sx={{ fontWeight: 'bold' }}>
                          {`${item?.updatedBy?.firstName || ''} ${item?.updatedBy?.lastName || ''}`}
                        </Typography>
                        <Box>
                          <Divider orientation="vertical" sx={{ mx: 1 }} />
                        </Box>
                        {item.updatedAt && item.updatedAt !== item.createdAt && (
                          <Typography color={theme.palette.Text.Secondary} sx={{ fontWeight: 'bold' }}>
                            Edited:&nbsp;
                          </Typography>
                        )}
                        <Typography color={theme.palette.Text.Secondary}>
                          {moment(
                            ((item.updatedAt !== item.createdAt ? item.updatedAt : item.createdAt) || 0) * 1000,
                          ).format('MMMM D, YYYY h:mm A')}
                        </Typography>
                      </Stack>
                    </Stack>
                    {canEditDetails && (
                      <>
                        <Button size="medium" variant="iconSubtle" onClick={e => handleMoreMenuClick(e, item)}>
                          <Icon name="OMore" />
                        </Button>
                        <ActionSheetWrapper
                          anchorEl={menuState.anchorEl}
                          open={Boolean(menuState.anchorEl)}
                          onClose={() => setMenuState({ anchorEl: null, selectedNoteItem: null })}
                          transitionDuration={0}
                        >
                          <Box>
                            <ActionSheetItem onClick={handleEditNote}>Edit</ActionSheetItem>
                            <ActionSheetItem divider />
                            <ActionSheetItem onClick={handleDeleteNote}>Delete</ActionSheetItem>
                          </Box>
                        </ActionSheetWrapper>
                      </>
                    )}
                  </Stack>
                  {index !== rightInvocationNotes.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
              ))
            ) : (
              <Typography color={theme.palette.Text.Secondary}>None</Typography>
            )}
          </Stack>
          {canEditDetails && (
            <Button
              color="tertiary"
              sx={{ whiteSpace: 'nowrap' }}
              onClick={() => setState({ isNoteModalOpen: true, selectedNote: '' })}
            >
              Add note
            </Button>
          )}
        </Stack>
      </InfoRow>
      {isNoteModalOpen && (
        <NoteModal
          note={selectedNote}
          onSubmit={handleSubmitNote}
          onClose={() => setState({ isNoteModalOpen: false })}
        />
      )}
    </Box>
  )
}
